import { onWindowResize, currentBreakpoint, currentBreakpointIndex, currentWindowHeight } from './utils/windowResize';
import { onScroll, scrollTop } from './utils/scroll';
import { onReady } from './utils/onReady';
import { setupNav, nav } from './components/nav';
import { setupLazyLoading } from './utils/lazyImage';
import { setupAccordions } from './components/accordion';
import { setupVideos } from './components/video';
import { initVhUnitOverwrite, debounce, isIE11 } from './utils/helpers';
import { version } from '../../package.json';
import { setupAnchorLinkScroll } from './components/anchors';
import { setupFilters } from './components/filter';
import { setupOverlay } from './components/overlay';
import { setupTabs } from './components/tabs';
import { setupYTVideos } from './components/video-yt';
import { setupSliders } from './components/slider';
import { watchFormFields } from './components/form';
import { setupIntersect } from './components/intersect';
import { setupCaseEntrance } from './components/caseentrance';
import { setupHeader } from './components/header';
import { setupPageLoadAnimation } from './components/page-load-animation';
import barba from '@barba/core';
import { gsap } from 'gsap';
import { setupTimeline } from './components/timeline';
import { setupMap, setupMapV2 } from './components/map';
import { scrollTo } from './utils/scrollTo';
import { setupTestMap } from './components/test.map';

function init() {
    const isTestTemplate = document.body.hasAttribute('data-test');
    const templateNames = ['frontpage', 'landingpage', 'standardpage'];
    const pageTransitionColours = ['red', 'green--dark', 'green--light', 'green--light', 'green--dark', 'red', 'green--dark', 'green--light'];
    const pageTransitionElm = document.querySelector('.page-transition');
    const footer = document.querySelector('.footer');

    let pageUrl = window.location.href;
    let pagePath = window.location.pathname;
    let pageTitle = document.title;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        'event': 'pageload',
        'pageurl': pageUrl,
        'pagepath': pagePath,
        'PageTitle': pageTitle
    });

    const scrollMarks = [25, 50, 75, 90, 100];
    let scrollMarksTracked = [];
    const body = document.body,
        html = document.documentElement;

    let documentHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);

    function switchTemplate(newTemplateName) {
        if (newTemplateName != document.body.getAttribute('data-template-name')) {
            templateNames.forEach(templateName => {
                if (newTemplateName != templateName && document.body.getAttribute('data-template-name') == templateName) {
                    document.body.classList.remove(`page-template-${templateName}`);
                    document.body.classList.add(`page-template-${newTemplateName}`);
                    document.body.setAttribute('data-template-name',newTemplateName);
                }
            });
        }
    }

    // Remove standby - we'ere ready now!
    document.body.classList.remove('standby');

    // Detect touch
    const isTouch = 'ontouchstart' in window ? true : false;
    document.body.setAttribute('data-is-touch', isTouch);

    if (isIE11) {
        document.body.classList.add('js-ie-11');
    }

    function setupBarba() {
        let colourCounter = 0;
        let colour;

        barba.init({
            prevent: ({ el }) => el.href.indexOf('Admin/Public/DWSDownload') != -1,
            transitions: [{
                name: 'default-transition',
                before(data) {
                    colour = pageTransitionColours[colourCounter];

                    if (colourCounter + 1 < pageTransitionColours.length) {
                        colourCounter++;
                    } else {
                        colourCounter = 0;
                    }

                    pageTransitionElm.classList.add(`page-transition--${colour}`);

                    // Remove nav--open from nav - we don't want to show it when the page has shiftet
                    nav.classList.remove('nav--open');

                    return gsap.timeline().to(data.current.container, {
                        transform: 'translateY(-310px) translateZ(0)',
                        duration: 0.8,
                        ease: 'power4.out',
                    }).to(footer, {
                        transform: 'translateY(-310px) translateZ(0)',
                        duration: 0.8,
                        ease: 'power4.out',
                    }, 0).to(pageTransitionElm, {
                        transform: 'translateY(0) translateZ(0)',
                        duration: 0.8,
                        ease: 'power4.out',
                    }, 0).set(footer, {
                        transform: 'translateZ(0)',
                    }).set(nav, {
                        transform: 'translateZ(0)',
                    });
                },
                beforeEnter(data) {

                },
                enter(data) {
                    document.documentElement.scrollTop = 0;
                    document.body.scrollTop = 0;

                    let pageTemplateName;

                    if (data.next.html.indexOf('header--frontpage')) {
                        pageTemplateName = 'frontpage';
                    }

                    if (data.next.html.indexOf('header--landingpage') != -1) {
                        pageTemplateName = 'landingpage';
                    }

                    if (data.next.html.indexOf('header--standardpage') != -1) {
                        pageTemplateName = 'standardpage';
                    }

                    // Switch to the right template if the new page uses a different template than the current one
                    switchTemplate(pageTemplateName);
                },
                after(data) {
                    setupComponents(false);

                    return gsap.timeline().to(pageTransitionElm, {
                        transform: 'translateY(-100vh) translateZ(0)',
                        duration: 0.8,
                        ease: 'power4.out',
                    }).from(data.next.container, {
                        transform: 'translateY(310px) translateZ(0)',
                        duration: 0.6,
                        ease: 'power4.out',
                    }, 0).from(footer, {
                        transform: 'translateY(310px) translateZ(0)',
                        duration: 0.6,
                        ease: 'power4.out',
                    }, 0).set(pageTransitionElm, {
                        transform: 'translateY(100vh) translateZ(0)',
                    }).then(function () {
                        pageTransitionElm.classList.remove(`page-transition--${colour}`);
                        data.next.container.removeAttribute('style');
                        document.body.classList.remove('js-scrolled');

                        if (window.location.hash.length > 0) {
                            const hashTarget = document.querySelector(`#module${window.location.hash.substring(1)}`);

                            if (hashTarget) {
                                scrollTo(parseInt(hashTarget.getBoundingClientRect().top + scrollTop), 1500);
                            }
                        }

                        pageUrl = window.location.href;
                        pagePath = window.location.pathname;
                        pageTitle = document.title;

                        window.dataLayer.push({
                            'event': 'pageload',
                            'pageurl': pageUrl,
                            'pagepath': pagePath,
                            'PageTitle': pageTitle
                        });

                        scrollMarksTracked = [];
                        documentHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
                    });
                }
            }]
        });
    }

    onReady(() => {
        initVhUnitOverwrite();

        const includePageLoadAnimation = document.body.classList.contains('page-template-frontpage');
        setupComponents(includePageLoadAnimation);

        const primaryNav = nav.querySelector('.nav__primary');

        if (primaryNav) {
            const navStyles = getComputedStyle(primaryNav);

            if (pageTransitionElm && footer && navStyles.opacity != 0 && !isIE11) {
                setupBarba();
            }
        }
    });

    function setupComponents(includePageLoadAnimation) {
        setupNav('.nav', false);

        setupAnchorLinkScroll();

        if (includePageLoadAnimation) {
            setupPageLoadAnimation();
        }

        setupLazyLoading();

        setupHeader();

        setupAccordions();

        setupFilters();

        setupOverlay();

        setupVideos();

        setupYTVideos();

        setupTabs();

        setupSliders(true);

        watchFormFields();

        setupIntersect();

        setupCaseEntrance();

        setupTimeline();

        setupMap();
        setupMapV2();

        setupTestMap();
    }


    onScroll(() => {
        scrollMarks.forEach(scrollMark => {
            if (!scrollMarksTracked.includes(scrollMark)) {
                const currentScroll = parseInt(scrollTop + currentWindowHeight);
                const checkScroll = (documentHeight / 100) * scrollMark;

                if (currentScroll >= checkScroll) {
                    window.dataLayer.push({
                        'event':'scrolldepth',
                        'eventValue': `${scrollMark}%`
                    });

                    scrollMarksTracked.push(scrollMark);
                }
            }
        });
    });

    window.dataLayer.push({
        'event': 'scriptLoaded'
    });
}

init();